/**
 * Chat Types
 * Types for AI chat functionality with trd-agent backend
 */

import type { UIMessage } from "ai";

/**
 * Agent types available in the system
 * Used across DTOs, services, and orchestrator for consistency
 */
export type AgentType =
  | "reports"
  | "web_search"
  | "search"
  | "pricing"
  | "general";

/**
 * Routing method used by the orchestrator
 */
export type RoutingMethod = "keyword" | "llm";

/**
 * Conversation metadata
 */
export interface Conversation {
  id: string;
  title: string;
  businessId: string;
  userId: string;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  messageCount: number;
  lastMessage?: string;
  lastAgentType?: AgentType;
  isOptimistic?: boolean; // True if conversation is locally created, pending backend confirmation
}

/**
 * Message metadata from backend
 */
export interface MessageMetadata {
  agentType?: AgentType;
  routingMethod?: RoutingMethod;
  conversationId?: string;
  toolCalls?: ToolCall[];
}

/**
 * Tool call information
 */
export interface ToolCall {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  result?: unknown;
}

/**
 * Extended UIMessage with custom metadata
 */
export interface ChatMessage extends UIMessage {
  metadata?: MessageMetadata;
  createdAt?: string; // ISO 8601 timestamp
}

/**
 * Request body for sending messages to trd-agent
 */
export interface SendMessageRequest {
  messages: ChatMessage[];
  conversationId?: string;
  metadata?: {
    preferredAgent?: AgentType;
  };
}

/**
 * Response headers from trd-agent
 */
export interface ChatResponseHeaders {
  "X-Conversation-Id"?: string;
  "X-Agent-Type"?: AgentType;
  "X-Routing-Method"?: RoutingMethod;
}

/**
 * Agent information for UI
 */
export interface AgentInfo {
  type: AgentType;
  label: string;
  description: string;
  color: string;
  badgeVariant: "info" | "success" | "default";
}

/**
 * Agent metadata mapping
 */
export const AGENT_INFO: Record<AgentType, AgentInfo> = {
  reports: {
    type: "reports",
    label: "Reportes",
    description: "Análisis de datos, generación de reportes y documentos",
    color: "blue",
    badgeVariant: "info",
  },
  web_search: {
    type: "web_search",
    label: "Web Search",
    description: "Búsqueda en internet y extracción de información",
    color: "green",
    badgeVariant: "success",
  },
  search: {
    type: "search",
    label: "Búsqueda",
    description: "Búsqueda en la base de datos de productos e inventario",
    color: "purple",
    badgeVariant: "info",
  },
  pricing: {
    type: "pricing",
    label: "Precios",
    description: "Consultas sobre precios, listas y cotizaciones",
    color: "orange",
    badgeVariant: "info",
  },
  general: {
    type: "general",
    label: "General",
    description: "Conversación general sin herramientas especializadas",
    color: "gray",
    badgeVariant: "default",
  },
};

/**
 * Example questions for empty state
 */
export interface ExampleQuestion {
  text: string;
  agent: AgentType;
}

export const EXAMPLE_QUESTIONS: ExampleQuestion[] = [
  {
    text: "¿Cuál es el flujo de caja proyectado para los próximos 30 días?",
    agent: "reports",
  },
  {
    text: "Busca información sobre las últimas tendencias en gestión de inventario",
    agent: "web_search",
  },
  {
    text: "Muestra los productos con stock bajo en el almacén principal",
    agent: "reports",
  },
  {
    text: "¿Qué clientes tienen deudas pendientes?",
    agent: "reports",
  },
];
